import type React from 'react';
import { useCallback, useState } from 'react';

import { Box, Button, Heading, Paragraph } from '@zillow/constellation-10';
import { LoadingButton, LoadingHeading, LoadingText } from '@zg-rentals/loading-components';
import { LeaseArtifactType } from '@zg-rentals/ts-leasing-client';

import type { Lease } from '../../../models';
import { Section } from '../../../models';
import SectionWrapper from '../../../components/SectionWrapper';
import { SectionProvider } from '../../../contexts/SectionContext';
import { LeaseTypes } from '../../../models/Types';
import LeaseSummary from '../../../components/LeaseSummary';
import { STRING_CONSTANTS } from './constants';
import {
  useGetNextNodeMutation,
  useLeasingStatusQuery,
  useReevaluateLeaseCompletionMutation,
  useSubmitWizardDataMutation,
} from '../../../utils/queries';

type Props = {
  userHasStartedWizard: boolean | null;
  setUserHasStartedWizard: (hasStarted: boolean | null) => void;
  leaseId: string;
  lease: Lease;
};

function SummarizeTheLease({ leaseId, lease, setUserHasStartedWizard, userHasStartedWizard }: Props) {
  const [showWizard, setShowWizard] = useState(false);
  const [section, setSection] = useState<Section | null>(null);
  const reevaluateLeaseCompletionMutation = useReevaluateLeaseCompletionMutation();
  const submitWizardDataMutation = useSubmitWizardDataMutation();
  const leaseStatusQuery = useLeasingStatusQuery(leaseId, { enabled: false });
  const getNextNodeMutation = useGetNextNodeMutation();

  function closeWizard() {
    setSection(null);
    setShowWizard(false);
  }

  async function onStartWizardClick() {
    setShowWizard(true);
    setUserHasStartedWizard(true);

    await leaseStatusQuery.refetch();
    if (leaseStatusQuery.data) {
      setSection(leaseStatusQuery.data);
    }
  }

  const submitDataHandler = useCallback(
    async (sectionToSubmit: Section): Promise<void> => {
      const data = sectionToSubmit.getSubmitJSON();
      await submitWizardDataMutation.mutateAsync({
        postData: { ...data, leaseId: leaseId as unknown as number },
      });

      if (sectionToSubmit.hasNext) {
        const response = await getNextNodeMutation.mutateAsync({
          currentNodeId: sectionToSubmit.currentNodeId,
          leaseId,
        });
        const nextSection = new Section(response);
        setSection(nextSection);
      } else {
        setSection(null);
        setShowWizard(false);
      }
    },
    [leaseId, submitWizardDataMutation, getNextNodeMutation],
  );

  async function onLastSectionSubmitted() {
    try {
      await reevaluateLeaseCompletionMutation.mutateAsync({
        postData: {
          leaseArtifactType: LeaseArtifactType.LEASE,
          leaseArtifactId: leaseId as unknown as number,
        },
      });
    } catch {
      // Mutation failed but we still close the wizard
    }
    closeWizard();
  }

  function renderLeaseContent(): React.ReactNode {
    const isSignedSelfUpload = lease?.leaseType === LeaseTypes.SIGNED_SELF_UPLOAD;

    return (
      <Box css={{ marginTop: 'tight', marginBottom: 'tight' }}>
        {userHasStartedWizard === null ? (
          <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tight' }}>
            <LoadingHeading
              isLoading
              level={3}
              css={{
                textStyle:
                  'responsive.body-lg-bold' /* eslint-disable-line c11nLint/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
              }}
              loadingWidth="40%"
            >
              Loading
            </LoadingHeading>
            <LoadingText isLoading loadingWidth="60%">
              Loading
            </LoadingText>
            <LoadingButton isLoading loadingWidth="15%">
              Continue
            </LoadingButton>
          </Box>
        ) : userHasStartedWizard && !showWizard ? (
          <LeaseSummary
            isAlwaysExpanded
            supportEdit
            isColandlord={false}
            categoryDescriptionOverrides={
              isSignedSelfUpload
                ? STRING_CONSTANTS.LANDLORD_CATEGORY_DESCRIPTION_SIGNED_LEASE_OVERRIDES
                : STRING_CONSTANTS.LANDLORD_CATEGORY_DESCRIPTION_OVERRIDES
            }
          />
        ) : isSignedSelfUpload ? (
          <>
            <Heading
              level={3}
              css={{
                textStyle:
                  'responsive.body-lg-bold' /* eslint-disable-line c11nLint/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
              }}
            >
              Provide your lease information
            </Heading>
            <Paragraph css={{ marginBottom: 'tight' }}>
              Enter your lease terms and make any necessary corrections to information that’s already filled in.
            </Paragraph>
            <Button tone="brand" emphasis="filled" onClick={onStartWizardClick}>
              Continue
            </Button>
          </>
        ) : (
          <>
            <Heading
              level={3}
              css={{
                textStyle:
                  'responsive.body-lg-bold' /* eslint-disable-line c11nLint/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
              }}
            >
              Tell us about your lease
            </Heading>
            <Paragraph css={{ marginBottom: 'tight' }}>
              You will need the names and emails of the people signing and the date it expires.
            </Paragraph>
            <Button tone="brand" emphasis="filled" onClick={onStartWizardClick}>
              Continue
            </Button>
          </>
        )}
      </Box>
    );
  }

  return (
    <>
      <SectionProvider initialSection={section}>
        <SectionWrapper
          modalIsOpen={showWizard}
          modalOnClose={closeWizard}
          onLastSectionSubmitted={onLastSectionSubmitted}
          onSubmitMiniWorkflow={submitDataHandler}
          modalHeading="Lease summary"
          renderModalView
        />
      </SectionProvider>
      {renderLeaseContent()}
    </>
  );
}

export default SummarizeTheLease;
